import Configuration from '@/agilis/util/Configuration'
import Vue from 'vue'
import { mapActions, mapGetters } from 'vuex'
import deleteMixin from '@/agilis/components/DataTableV2/common/deleteMixin'
import FormDivider from '@/agilis/components/Form/FormDivider/FormDivider.vue'
import L from 'lodash'
import UnStyledLink from '@/agilis/components/UnstyledLink/UnstyledLink.vue'
import TrackerDataTableItemDialogEdit
from '@/applications/scrum/components/TrackerDataTable/TrackerDataTableItemDialogEdit/TrackerDataTableItemDialogEdit.vue'

export default {
  name: 'TrackerDataTableItemChildren',
  mixins: [deleteMixin],

  components: {
    TrackerDataTableItemDialogEdit,
    UnStyledLink,
    FormDivider
  },

  props: {
    trackerDTO: Object,
    hideActions: Boolean,
    hideToolbar: Boolean,
    showDivider: Boolean
  },

  data () {
    return {
      trackerWithChildren: [],
      operationPost: 'post',
      openDialog: false,
      childrenTableConfigDefault: {
        columns: [
          {
            id: 'id',
            type: 'number',
            label: 'label.id'
          },
          {
            id: 'trackerTypeId',
            type: 'string',
            label: 'label.trackerType',
            renderMethod: 'renderTrackerTypeId'
          },
          {
            id: 'state',
            type: 'text',
            label: 'label.status',
            renderMethod: 'renderState'
          },
          {
            id: 'title',
            type: 'text',
            label: 'label.title'
          },
          {
            id: 'assignee',
            type: 'select',
            label: 'label.owner',
            itemText: 'fullName',
            itemTextExpr: '$firstName $lastName'
          },
          {
            id: 'updatedTime',
            type: 'datetime',
            label: 'label.updateTime',
            format: 'format.date'
          }
        ]
      },
      childrenItemDTO: {}
    }
  },

  mounted () {
    this.fetchChildren()
  },

  computed: {
    ...mapGetters({
      userId: 'userDataStore/userId',
      selectedContext: 'contextStore/selectedContext',
      applicationId: 'contextStore/applicationId',
      workspaceId: 'contextStore/workspaceId',
      trackerTypeId: 'contextStore/trackerTypeId',
      trackerType: 'contextStore/trackerType',
      trackerTypes: 'contextStore/trackerTypes',
      mode: 'contextStore/contextMode',
      trackerId: 'contextStore/trackerId',
      dataTableById: 'contextStore/trackerDataTableById'
    }),
    title () {
      const childrenLabel = this.trackerType.dataTableItemChildrenTitle
      return childrenLabel ? this.$t(childrenLabel) : this.$t('label.subTasks')
    },
    menuData () {
      // check what children types can be created by the user
      const childrenTypes = this.trackerType.childrenTypes
      const authPostTrackerTypes = Configuration.auth.getTrackerTypes(this.applicationId, this.workspaceId, this.operationPost)
      const configuredChildrenTypes = childrenTypes ?? Object.keys(authPostTrackerTypes)
      const authorizedChildTypeIds = Object.keys(authPostTrackerTypes)
      const menuItems = []

      configuredChildrenTypes.forEach(childType => {
        const childTypeId = this.getChildTrackerTypeId(childType)
        const childTypeConfig = authPostTrackerTypes[childTypeId]

        if (authorizedChildTypeIds.includes(childTypeId)) {
          menuItems.push({
            id: childTypeId,
            label: this.$t(childTypeConfig.name)
          })
        }
      })
      return menuItems
    },

    childrenHeader () {
      const columns = this.childColumnsConfig
      const hh = Object.values(columns).map((c) => {
        return {
          text: this.$t(c.label),
          value: c.id
        }
      })

      return [
        ...hh,
        this.hideActions ? null : { value: 'actions', text: 'Actions' }
      ].filter(Boolean)
    },

    childColumnsConfig () {
      return this.childrenTableConfig()?.columns
        ? this.childrenTableConfig().columns
        : this.childrenTableConfigDefault.columns
    },

    userCanOpenChildren () {
      return this.userCanAccessFeature('open')
    },

    sortByCriteria () {
      const childrenTableConfig = this.childrenTableConfig()
      const sortByConfig = childrenTableConfig?.sortBy
      return sortByConfig || 'id'
    },

    sortCriteria () {
      const childrenTableConfig = this.childrenTableConfig()
      const sortConfig = childrenTableConfig?.sortCriteria
      return sortConfig || 'ASC'
    },

    showExpand () {
      return this.childrenTableConfig()?.expandRows
    },

    expandRowsIsOpen () {
      return this.childrenTableConfig()?.expandRowsIsOpen
    },

    expandRowsArray () {
      return this.expandRowsIsOpen ? this.trackerWithChildren.childTrackers : []
    }
  },

  methods: {
    ...mapActions({
      'notifyError': 'notificationStore/notifyError',
      'startLoader': 'loaderStore/startLoader',
      'stopLoader': 'loaderStore/stopLoader'
    }),

    getChildTrackerTypeId (childType) {
      return typeof childType === 'object' ? childType?.trackerTypeId : childType
    },

    childrenTableConfig (item) {
      const childrenTableConfiguration = L.cloneDeep(Configuration.simpleTable.getChildrenTable(this.applicationId, this.trackerTypeId))
      if (item) {
        childrenTableConfiguration.formRows = this.dataTableById(item.trackerTypeId)?.formRows
        childrenTableConfiguration.columns = L.merge({}, this.childColumnsConfig, this.dataTableById(item.trackerTypeId).columns)
      }
      return childrenTableConfiguration
    },

    fetchChildren () {
      this.startLoader({ id: 'tracker-subtasks' })
      const url = Vue.axios.agilisUrlTrackerChildren(
        this.applicationId,
        this.trackerTypeId,
        this.trackerId)
      Vue.axios
        .get(
          url,
          Vue.axios.agilisRequestConfig(url))
        .then((response) => {
          this.trackerWithChildren = response.data
          if (this.trackerWithChildren.childTrackers) {
            this.trackerWithChildren.childTrackers = this.trackerWithChildren.childTrackers.sort((a, b) => {
              if (this.sortCriteria === 'ASC') {
                return a.tracker[this.sortByCriteria] > b.tracker[this.sortByCriteria] ? 1 : -1
              }
              return a.tracker[this.sortByCriteria] < b.tracker[this.sortByCriteria] ? 1 : -1
            })
            const children = this.trackerWithChildren.childTrackers.map(c => c.tracker)
            this.$emit('childrenLoaded', children)
          }
        })
        .catch((error) => {
          this.notifyError(error)
        })
        .finally(() => {
          this.stopLoader({ id: 'tracker-subtasks' })
        })
    },

    handleChildClick (childTracker) {
      this.$router.agilis.viewTracker({
        trackerId: childTracker.id,
        trackerTypeId: childTracker.trackerTypeId,
        parentTrackerId: this.trackerId
      })
    },

    handleNewChildClick (childTrackerTypeId) {
      this.$router.agilis.newTracker(childTrackerTypeId, this.trackerId, this.trackerDTO)
    },

    handleEditItem (item) {
      this.openDialog = true
      this.childrenItemDTO = item
    },
    childTrackerUrl (childTracker) {
      return this.$router.agilis.urlViewTracker({
        trackerId: childTracker.id,
        trackerTypeId: childTracker.trackerTypeId
      })
    },

    onDeleteItemDone () {
      this.fetchChildren()
    },

    renderChildColumn (colId, childTracker) {
      const colConfig = this.childColumnsConfig.find(childColumns => childColumns.id === colId)
      if (colConfig) return Configuration.datatable.renderColumn(colConfig, childTracker)
    },

    userCanAccessFeature (operation) {
      return Configuration.auth.userCanAccessFeature(
        this.selectedContext, 'subtask', operation, this.trackerDTO, this.userId)
    },

    groupName (group) {
      return this.$t(Configuration.getTrackerType(this.applicationId, group).name)
    },

    groups (children) {
      return L.uniq(
        children.map(child => {
          return child.tracker.trackerTypeId
        })
      )
    }
  }
}
